/**
 * AuthContext - Gestione autenticazione e stato utente
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getCurrentUser,
  logout as logoutService,
  getUserDataFromStorage,
  isAuthenticated,
} from '../services/authService';
import { initializeAllCache } from '../services/crmCacheService';
import { STORAGE_KEYS } from '../utils/constants';
import { decodeJWT, getTokenTimeRemaining } from '../utils/helpers';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere usato all\'interno di AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);

  // useRef per mantenere reference stabile e prevenire memory leak
  const handleLogoutRef = useRef();
  // Guard per prevenire chiamate logout sovrapposte (evita loop con interceptor)
  const isLoggingOutRef = useRef(false);

  /**
   * Effettua il logout
   */
  const handleLogout = useCallback(async () => {
    // Previeni chiamate logout multiple/sovrapposte che causano loop infiniti
    if (isLoggingOutRef.current) {
      logger.log('[AuthContext] Logout già in corso, ignoro chiamata duplicata');
      return;
    }
    isLoggingOutRef.current = true;

    try {
      logger.log('[AuthContext] Logout iniziato');
      // Chiama il servizio di logout (endpoint protetto)
      await logoutService();

      // Pulisce lo stato utente
      setUser(null);

      logger.log('[AuthContext] Logout completato');
      return { success: true };
    } catch (error) {
      logger.error('[AuthContext] Errore durante il logout:', error);

      // Pulisce comunque lo stato utente
      setUser(null);

      return { success: false, message: error.message };
    } finally {
      isLoggingOutRef.current = false;
    }
  }, []);

  /**
   * Inizializza l'autenticazione al mount
   * Ripristina la sessione se esiste un token valido
   */
  useEffect(() => {
    logger.log('[AuthContext] ===== INIT AUTH useEffect =====');

    const initAuth = async () => {
      try {
        logger.log('[AuthContext] Inizio initAuth...');

        // Verifica se esiste un token
        const hasToken = isAuthenticated();
        logger.log('[AuthContext] isAuthenticated():', hasToken);

        if (hasToken) {
          // Recupera i dati utente dal localStorage per mostrarli subito
          const cachedUser = getUserDataFromStorage();
          logger.log('[AuthContext] Dati utente dalla cache:', cachedUser ? 'PRESENTI' : 'ASSENTI');

          if (cachedUser) {
            logger.log('[AuthContext] Imposto user dalla cache');
            setUser(cachedUser);
          }

          // SEMPRE valida il token chiamando il backend
          // Questo verifica che il token sia ancora presente in t_registry
          logger.log('[AuthContext] ===== INIZIO VALIDAZIONE TOKEN CON BACKEND =====');
          const response = await getCurrentUser();
          logger.log('[AuthContext] Risposta getCurrentUser - success:', response.success, 'code:', response.code);

          if (response.success) {
            logger.log('[AuthContext] Token valido, user aggiornato');
            setUser(response.data);
          } else {
            // Token non valido (rimosso da t_registry), effettua logout
            logger.warn('[AuthContext] ===== TOKEN NON VALIDO - LOGOUT =====');
            logger.warn('[AuthContext] Response:', response);
            await handleLogout();
          }
        } else {
          logger.log('[AuthContext] Nessun token trovato, utente non autenticato');
        }
      } catch (error) {
        logger.error('[AuthContext] ===== ERRORE INIT AUTH =====');
        logger.error('[AuthContext] Errore:', error);
        logger.error('[AuthContext] Status:', error.response?.status);
        logger.error('[AuthContext] Data:', error.response?.data);
        // Se getCurrentUser fallisce (es. 401), fai logout
        logger.error('[AuthContext] Chiamo handleLogout() per errore');
        await handleLogout();
      } finally {
        logger.log('[AuthContext] Termine initAuth - setLoading(false)');
        setLoading(false);
        setIsAuthReady(true);
      }
    };

    initAuth();
  }, [handleLogout]);

  /**
   * Aggiorna il ref quando handleLogout cambia
   */
  useEffect(() => {
    handleLogoutRef.current = handleLogout;
  }, [handleLogout]);

  /**
   * Listener per evento logout globale (da api.js interceptor)
   * Fix memory leak: registra l'event listener UNA SOLA VOLTA
   */
  useEffect(() => {
    const handleGlobalLogout = () => {
      logger.log('[AuthContext] Evento auth:logout ricevuto, eseguo logout');
      if (handleLogoutRef.current) {
        handleLogoutRef.current();
      }
    };

    window.addEventListener('auth:logout', handleGlobalLogout);

    return () => {
      window.removeEventListener('auth:logout', handleGlobalLogout);
    };
  }, []); // ← Array vuoto: si registra UNA SOLA VOLTA

  /**
   * ✅ NUOVO: Sync automatico localStorage quando user cambia
   * localStorage è SOLO per persistenza, Context è la source of truth
   */
  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      logger.log('[AuthContext] User sincronizzato in localStorage');
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    }
  }, [user]);

  /**
   * Effettua il login e salva i dati utente
   * @param {Object} loginData - Dati ritornati dal login (accessToken, user, etc.)
   * Fix: Rimosso doppio setUser per evitare re-rendering inutile
   */
  const login = useCallback(async (loginData) => {
    try {
      logger.log('[AuthContext] Login iniziato');

      // Recupera i dati completi dell'utente dal backend
      // (il token è già stato salvato dal servizio di login)
      logger.log('[AuthContext] Chiamata getCurrentUser...');
      const response = await getCurrentUser();
      logger.log('[AuthContext] Risposta getCurrentUser:', response);

      if (response.success) {
        // Imposta user UNA SOLA VOLTA con i dati completi
        logger.log('[AuthContext] Impostazione user con dati completi');
        setUser(response.data);

        // Trigger evento custom per notificare altri componenti
        logger.log('[AuthContext] Trigger evento auth:login');
        window.dispatchEvent(new CustomEvent('auth:login', { detail: response.data }));

        logger.log('[AuthContext] Login completato con successo');
        return { success: true };
      } else {
        throw new Error(response.message || 'Login fallito');
      }
    } catch (error) {
      logger.error('[AuthContext] Errore durante il login:', error);
      return { success: false, message: error.message };
    }
  }, []);

  /**
   * Aggiorna i dati utente
   * @param {Object} updatedData - Dati aggiornati
   */
  const updateUser = useCallback((updatedData) => {
    setUser((prev) => ({ ...prev, ...updatedData }));
    // ✅ localStorage sync gestito da useEffect automatico
  }, []);

  /**
   * Ricarica i dati utente dal backend
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await getCurrentUser();

      if (response.success) {
        logger.log('[AuthContext] refreshUser - Dati utente ricevuti:', response.data);
        logger.log('[AuthContext] refreshUser - idAnagrafico:', response.data?.idAnagrafico);
        setUser(response.data);

        // ✅ Inizializza TUTTA la cache CRM in background (non bloccante)
        // Include: Enti, Iniziative, Prenotazioni, Presenze, Conteggi
        initializeAllCache().then((cacheResponse) => {
          if (cacheResponse.success) {
            logger.log('[AuthContext] refreshUser - ✅ Cache CRM completa inizializzata:', cacheResponse.data);
          } else {
            logger.warn('[AuthContext] refreshUser - ⚠️ Errore inizializzazione cache CRM:', cacheResponse.message);
          }
        }).catch((err) => {
          logger.error('[AuthContext] refreshUser - ❌ Errore durante inizializzazione cache CRM:', err);
        });

        return { success: true, data: response.data };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      logger.error('Errore durante il refresh dei dati utente:', error);
      return { success: false, message: error.message };
    }
  }, []);

  /**
   * Verifica se l'utente ha un determinato ruolo
   * @param {string|string[]} requiredRoles - Ruolo/i richiesto/i
   * @returns {boolean}
   */
  const hasRole = useCallback(
    (requiredRoles) => {
      if (!user || !user.roles) return false;

      const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
      const userRoles = user.roles.map((r) => r.toLowerCase());

      // Admin ha sempre accesso
      if (userRoles.includes('admin')) return true;

      return roles.some((role) => userRoles.includes(role.toLowerCase()));
    },
    [user]
  );

  /**
   * Ottiene il tempo rimanente prima della scadenza del token
   * @returns {number} Millisecondi rimanenti
   */
  const getSessionTimeRemaining = useCallback(() => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) return 0;

    return getTokenTimeRemaining(token);
  }, []);

  const value = {
    user,
    loading,
    isAuthReady,
    isAuthenticated: !!user,
    login,
    logout: handleLogout,
    updateUser,
    refreshUser,
    hasRole,
    getSessionTimeRemaining,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
